import type { SiteSections } from "@ecliptica/config/site-sections";
import { CardImage } from "@ecliptica/game-ui";
import { useTranslation, type Lang } from "@ecliptica/i18n";
import { Button } from "@ecliptica/ui";
import { HERO_CARDS } from "../../content/site";
import { Link } from "react-router";
import { sitePath } from "../../lib/lang";
import { downloadPanels } from "../../lib/sections";
import { HeroVideo } from "./HeroVideo";
import { TrailerButton } from "./TrailerButton";

const TILT = ["-rotate-[9deg] translate-x-7 translate-y-4", "z-10 -translate-y-3.5 scale-110", "rotate-[9deg] -translate-x-7 translate-y-4"];

export function Hero({ lang, sections }: { lang: Lang; sections: SiteSections }) {
  const { t } = useTranslation();
  return (
    <section id="top" className="relative isolate grid min-h-dvh items-center gap-6 grid-cols-[minmax(0,1fr)] overflow-hidden px-4 pb-12 pt-24 text-center sm:px-[6vw] md:grid-cols-[1.2fr_1fr] md:pt-28 md:text-left">
      <HeroVideo />
      <div aria-hidden className="absolute inset-0 -z-10 bg-night mix-blend-color" />
      <div aria-hidden className="absolute inset-0 -z-10 bg-[linear-gradient(180deg,rgba(7,8,15,.7),rgba(7,8,15,.55))] md:bg-[linear-gradient(90deg,rgba(7,8,15,.9)_0%,rgba(7,8,15,.65)_45%,rgba(7,8,15,.2)_100%)]" />
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-[0.08em] text-gold sm:text-[13px] sm:tracking-[0.2em]">{t("hero.tag")}</p>
        <h1 className="my-3 whitespace-nowrap bg-gradient-to-b from-gold-light via-gold to-gold-deep bg-clip-text pb-1 text-[clamp(24px,7.4vw,60px)] md:text-[clamp(48px,4.2vw,104px)] font-extrabold leading-none tracking-[0.16em] text-transparent drop-shadow-[0_2px_18px_rgba(7,8,15,.6)]">
          ECLIPTICA
        </h1>
        <p aria-hidden className="mb-4 font-title text-[10px] font-semibold tracking-[0.5em] text-mist sm:text-[13px] sm:tracking-[0.6em]">
          {t("hero.tagline")}
        </p>
        <p className="mx-auto max-w-[620px] text-base text-moon sm:text-[19px] md:mx-0">{t("hero.lead")}</p>
        <div className="mt-6 flex flex-wrap justify-center gap-3.5 md:justify-start">
          {/* Botões só para secções ligadas (backoffice → Site). */}
          {downloadPanels(sections).length > 0 && (
            <Button asChild variant="primary" size="lg">
              <a href="#download">{t("hero.play")}</a>
            </Button>
          )}
          <TrailerButton />
          {sections.world && (
            <Button asChild size="lg">
              <Link to={sitePath(lang, "world")}>{t("hero.world")}</Link>
            </Button>
          )}
        </div>
        <p className="mt-2.5 text-[13px] text-muted-foreground">{t("hero.soon")}</p>
      </div>
      <div aria-hidden className="group flex justify-center">
        {HERO_CARDS.map((id, i) => (
          <CardImage
            key={id}
            id={id}
            lang={lang}
            alt=""
            loading="lazy"
            fetchPriority="low"
            width={300}
            height={420}
            className={`w-[30%] max-w-[230px] transition-transform duration-500 ${TILT[i]}`}
          />
        ))}
      </div>
    </section>
  );
}
